import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import ProjectCard from "../components/ProjectCard";
import ProjectModal from "../components/ProjectModal";

const ITEMS_PER_PAGE = 6;

export default function Projects(){
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState({ q:"", tech:"", tag:"", featured:false });
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/data/projects.json");
        const data = await res.json();
        const sorted = [...data].sort((a,b)=>new Date(b.completion_date||0)-new Date(a.completion_date||0));
        setAll(sorted);
      } catch (err) {
        console.error("Failed to load projects:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const techs = useMemo(()=>{
    const s = new Set();
    all.forEach(p => (p.technologies||[]).forEach(t=>s.add(t)));
    return Array.from(s).sort();
  }, [all]);

  const tags = useMemo(()=>{
    const s = new Set();
    all.forEach(p => (p.tags||[]).forEach(t=>s.add(t)));
    return Array.from(s).sort();
  }, [all]);

  const filtered = all.filter(p=>{
    const qok = filters.q ?
      (p.title + " " + p.description + " " + (p.technologies||[]).join(" "))
        .toLowerCase()
        .includes(filters.q.toLowerCase())
      : true;
    const tok = filters.tech ? (p.technologies||[]).includes(filters.tech) : true;
    const gok = filters.tag ? (p.tags||[]).includes(filters.tag) : true;
    const fok = filters.featured ? p.featured : true;
    return qok && tok && gok && fok;
  });

  // Pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIdx = startIdx + ITEMS_PER_PAGE;
  const list = filtered.slice(startIdx, endIdx);

  const clearFilters = () => {
    setFilters({ q:"", tech:"", tag:"", featured:false });
    setCurrentPage(1);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const hasActiveFilters = filters.q || filters.tech || filters.tag || filters.featured;

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 min-h-screen">
      {/* Header */}
      <div className="border-b-2 border-slate-900 pb-6 mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-2">
          All Projects
        </h1>
        <p className="text-sm md:text-base font-mono text-slate-600 dark:text-slate-400">
          {loading ? "Loading..." : `${all.length} total • ${filtered.length} filtered • Page ${currentPage} of ${totalPages || 1}`}
        </p>
      </div>

      {/* Filters */}
      <div className="mb-8 space-y-4">
        {/* Search */}
        <div>
          <input
            className="w-full md:w-96 border-2 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-4 py-2 text-sm font-mono
                       focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
            placeholder="Search projects..."
            value={filters.q}
            onChange={(e)=>setFilters(f=>({...f,q:e.target.value}))}
          />
        </div>

        {/* Filter buttons */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Technology filter */}
          <select
            className="border-2 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 pl-3 pr-8 py-2 text-sm font-mono
                       focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all cursor-pointer
                       appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%23000%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] dark:bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%23fff%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px_12px] bg-[position:right_0.5rem_center] bg-no-repeat"
            value={filters.tech}
            onChange={(e)=>setFilters(f=>({...f,tech:e.target.value}))}
          >
            <option value="">All Technologies ({techs.length})</option>
            {techs.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          {/* Tag filter */}
          <select
            className="border-2 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 pl-3 pr-8 py-2 text-sm font-mono
                       focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all cursor-pointer
                       appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%23000%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] dark:bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%23fff%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px_12px] bg-[position:right_0.5rem_center] bg-no-repeat"
            value={filters.tag}
            onChange={(e)=>setFilters(f=>({...f,tag:e.target.value}))}
          >
            <option value="">All Categories ({tags.length})</option>
            {tags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          {/* Featured toggle */}
          <label className="flex items-center gap-2 border-2 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-all select-none">
            <input
              type="checkbox"
              checked={filters.featured}
              onChange={(e)=>setFilters(f=>({...f,featured:e.target.checked}))}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm font-mono text-slate-900 dark:text-slate-100">Featured only</span>
          </label>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-slate-900 text-white text-sm font-mono hover:bg-slate-700 transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Active filters display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 text-xs font-mono">
            {filters.q && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 border border-blue-300">
                Search: "{filters.q}"
              </span>
            )}
            {filters.tech && (
              <span className="px-2 py-1 bg-purple-100 text-purple-800 border border-purple-300">
                Tech: {filters.tech}
              </span>
            )}
            {filters.tag && (
              <span className="px-2 py-1 bg-green-100 text-green-800 border border-green-300">
                Category: {filters.tag}
              </span>
            )}
            {filters.featured && (
              <span className="px-2 py-1 bg-yellow-400 text-slate-900 border-2 border-slate-900 font-bold shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                ★ FEATURED
              </span>
            )}
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-20">
          <div className="animate-pulse text-4xl mb-4">⚙️</div>
          <p className="text-sm font-mono text-slate-600">Loading projects...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-20 border-2 border-dashed border-slate-300">
          <div className="text-4xl mb-4">🔍</div>
          <p className="text-lg font-bold text-slate-900 mb-2">No projects found</p>
          <p className="text-sm text-slate-600 mb-4">
            Try adjusting your filters or search terms
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Projects grid */}
      {!loading && list.length > 0 && (
        <>
          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {list.map((p, idx)=>(
              <motion.div
                key={p.title + p.completion_date}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
              >
                <ProjectCard p={p} onOpen={()=>setOpen(p)} />
              </motion.div>
            ))}
          </motion.div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t-2 border-slate-900 dark:border-slate-600 pt-6">
              <div className="flex flex-wrap items-center justify-center gap-2">
                {/* Previous button */}
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border-2 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm font-mono
                             hover:bg-slate-900 dark:hover:bg-slate-600 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-slate-800 disabled:hover:text-slate-900 dark:disabled:hover:text-slate-100"
                >
                  ← Prev
                </button>

                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-3 py-2 text-sm font-mono transition-colors ${
                      page === currentPage
                        ? 'bg-slate-900 dark:bg-slate-600 text-white border-2 border-slate-900 dark:border-slate-600'
                        : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border-2 border-slate-900 dark:border-slate-600 hover:bg-slate-900 dark:hover:bg-slate-600 hover:text-white'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                {/* Next button */}
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border-2 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm font-mono
                             hover:bg-slate-900 dark:hover:bg-slate-600 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-slate-800 disabled:hover:text-slate-900 dark:disabled:hover:text-slate-100"
                >
                  Next →
                </button>
              </div>

              {/* Page info */}
              <div className="text-center mt-4">
                <p className="text-xs font-mono text-slate-600 dark:text-slate-400">
                  Showing {startIdx + 1}-{Math.min(endIdx, filtered.length)} of {filtered.length} projects
                </p>
              </div>
            </div>
          )}
        </>
      )}

      <ProjectModal project={open} onClose={()=>setOpen(null)} />
    </section>
  );
}
